import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle } from 'lucide-react';

export function HowItWorksSection() {
  return (
    <section className="py-20 px-4 md:px-6 bg-muted/10 section-spacing">
      <div className="container">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Transforming Hiring</Badge>
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
            How It Works – Simplifying Hiring & Career Growth
          </h2>
          <p className="mt-4 max-w-3xl mx-auto text-muted-foreground md:text-lg">
            NewLoop reimagines the traditional hiring process to save time, reduce costs, and create better matches
          </p>
        </div>

        {/* Flow Chart Style Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <div className="bg-destructive/10 rounded-xl overflow-hidden border border-destructive/20">
            <div className="bg-destructive/20 p-4">
              <h3 className="text-xl font-bold flex items-center text-destructive/90">
                Traditional Hiring Process
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start border-l-2 border-destructive/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-destructive/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">Job Posting</h4>
                  <p className="text-xs text-muted-foreground">Creating and posting job descriptions across multiple platforms</p>
                </div>
              </div>
              <div className="flex items-start border-l-2 border-destructive/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-destructive/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">Resume Screening</h4>
                  <p className="text-xs text-muted-foreground">Manually reviewing hundreds of applications</p>
                </div>
              </div>
              <div className="flex items-start border-l-2 border-destructive/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-destructive/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">Initial Interviews</h4>
                  <p className="text-xs text-muted-foreground">Scheduling and conducting phone or initial screening interviews</p>
                </div>
              </div>
              <div className="flex items-start border-l-2 border-destructive/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-destructive/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">Technical Assessment</h4>
                  <p className="text-xs text-muted-foreground">Evaluating candidate abilities through tests or assignments</p>
                </div>
              </div>
            </div>
            <div className="bg-primary/10 p-4 flex justify-between items-center border-t border-primary/20">
              <div className="font-medium text-sm text-primary">80% Slower</div>
              <div className="font-medium text-sm text-primary">60% More Cost</div>
              <div className="font-medium text-sm text-primary">Lower Quality</div>
            </div>
          </div>
          
          <div className="bg-primary/10 rounded-xl overflow-hidden border border-primary/20">
            <div className="bg-primary/20 p-4">
              <h3 className="text-xl font-bold flex items-center text-primary/90">
                NewLoop Hiring Process
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start border-l-2 border-primary/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-primary/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">Define Role Needs</h4>
                  <p className="text-xs text-muted-foreground">Specify skills, experience, and career trajectory needed</p>
                </div>
              </div>
              <div className="flex items-start border-l-2 border-primary/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-primary/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">AI Talent Matching</h4>
                  <p className="text-xs text-muted-foreground">Algorithm identifies ideal candidates from our verified pool</p>
                </div>
              </div>
              <div className="flex items-start border-l-2 border-primary/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-primary/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">Direct Connection</h4>
                  <p className="text-xs text-muted-foreground">Connect and schedule interviews with matched candidates</p>
                </div>
              </div>
              <div className="flex items-start border-l-2 border-primary/30 pl-4 py-3 relative">
                <div className="absolute -left-[9px] top-4 w-4 h-4 rounded-full bg-primary/30"></div>
                <div className="ml-2">
                  <h4 className="font-medium text-sm">Seamless Hiring</h4>
                  <p className="text-xs text-muted-foreground">Manage the entire hiring process in one platform</p>
                </div>
              </div>
            </div>
            <div className="bg-destructive/10 p-4 flex justify-between items-center border-t border-destructive/20">
              <div className="font-medium text-sm text-destructive">80% Faster</div>
              <div className="font-medium text-sm text-destructive">60% Lower Cost</div>
              <div className="font-medium text-sm text-destructive">2x Better Matches</div>
            </div>
          </div>
        </div>

        {/* Why This Matters */}
        <div className="bg-background rounded-xl p-8 border shadow-md mb-16">
          <h3 className="text-xl font-bold mb-6 text-center">Why This Matters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <h4 className="font-semibold mb-2">For Professionals</h4>
              <p className="text-muted-foreground text-sm">
                Stop applying to hundreds of jobs. Get AI career guidance and let the right opportunities find you.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
              <h4 className="font-semibold mb-2">For Hiring Managers</h4>
              <p className="text-muted-foreground text-sm">
                Discover candidates based on skills and potential, not just keywords. Connect with professionals whose career trajectories align with your roles.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-4">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="font-semibold mb-2">For Companies</h4>
              <p className="text-muted-foreground text-sm">
                Build a strategic talent pipeline with professionals who are actually aligned with your needs, reducing time-to-hire and improving retention.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}